import { filterAcceptedAudios, normalizeItems } from './validate-audio.helpers';
import type { NormalizedAudio, ValidationApiItem } from './types';

/**
 * Non-visual rules of the "Valider les audios" session, kept pure so tests
 * lock the payloads sent to validationApi.validateAudio and the batch
 * transitions.
 */

/** Audios per status and per page requested from validationApi. */
export const VALIDATE_AUDIO_PAGE_SIZE = 10;

export type AudioDecisionPayload =
  | { audioId: string; action: 'accept'; audioGender: string }
  | { audioId: string; action: 'reject'; reason: string };

/** Body of an accept: the voice heard is required (checked by the caller). */
export const acceptPayload = (audioId: string, audioGender: string): AudioDecisionPayload => ({
  audioId,
  action: 'accept',
  audioGender,
});

/** Body of a reject: the detailed text wins over the picked reason when filled. */
export const rejectPayload = (audioId: string, rejectionReason: string, detailedReason: string): AudioDecisionPayload => ({
  audioId,
  action: 'reject',
  reason: detailedReason.trim() || rejectionReason,
});

/** The list returned by listByLangAndStatus, whatever envelope it came in. */
export function extractValidationItems(result: unknown): ValidationApiItem[] {
  const d = result as Record<string, unknown>;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.data)) return d.data;
  const inner = d?.data as Record<string, unknown> | undefined;
  if (Array.isArray(inner?.data)) return inner.data as ValidationApiItem[];
  return [];
}

/** One batch: pending audios, then accepted ones this person hasn't judged yet. */
export const mergeValidationBatch = (pendingResult: unknown, acceptedResult: unknown): NormalizedAudio[] => [
  ...normalizeItems(extractValidationItems(pendingResult)),
  ...filterAcceptedAudios(normalizeItems(extractValidationItems(acceptedResult))),
];

/** Marks the audio at `index` as accepted or rejected by `userId`. */
export function markDecision(audios: NormalizedAudio[], index: number, userId: string, action: 'accept' | 'reject'): NormalizedAudio[] {
  return audios.map((audio, idx) => {
    if (idx !== index) return audio;
    return action === 'accept'
      ? { ...audio, validatedBy: [...(audio.validatedBy || []), userId], hasValidated: true }
      : { ...audio, rejectedBy: [...(audio.rejectedBy || []), userId], hasRejected: true };
  });
}

/**
 * Going back clears, on screen only, the decision of the audio being left
 * (historical behaviour: nothing is sent to the API). `undone` says whether
 * the session counter must drop by one.
 */
export function undoDecisionOnLeave(audios: NormalizedAudio[], index: number): { audios: NormalizedAudio[]; undone: boolean } {
  const audio = audios[index];
  if (!audio?.hasValidated && !audio?.hasRejected) return { audios, undone: false };
  return {
    audios: audios.map((item, idx) => (idx === index ? { ...item, hasValidated: false, hasRejected: false } : item)),
    undone: true,
  };
}

/** Drops a deleted audio and keeps the cursor on a valid item. */
export function removeAudioAt(audios: NormalizedAudio[], index: number, cursor: number): { audios: NormalizedAudio[]; cursor: number } {
  const next = audios.filter((_, idx) => idx !== index);
  return { audios: next, cursor: Math.min(cursor, Math.max(0, next.length - 1)) };
}

export function decisionCounts(audios: NormalizedAudio[]) {
  const accepted = audios.filter((a) => a.hasValidated).length;
  const rejected = audios.filter((a) => a.hasRejected).length;
  return { accepted, rejected, decided: accepted + rejected };
}

/** A moderator's text correction, shown in place (spec 003). */
export function applyCorrectedTexts(
  audios: NormalizedAudio[],
  audioId: string,
  texts: { phraseLocal: string | null; phraseFr: string | null },
): NormalizedAudio[] {
  return audios.map((audio) =>
    audio.id === audioId
      ? { ...audio, phraseLocal: texts.phraseLocal ?? undefined, phraseFr: texts.phraseFr ?? undefined, word: texts.phraseLocal ?? audio.word }
      : audio,
  );
}
